/**
 * Order-form TP/SL controls: the checkbox and, once ticked, the two trigger-price
 * inputs attached to the order being placed.
 *
 * Presentational only — all state and the submit-time behaviour live in
 * `hooks/tpsl/useOrderFormTpsl`, so this renders whatever it is handed. Layout
 * comes from the shared tokens in style.tradeform, not inline styles, so this
 * block keeps the same rhythm as the Reduce Only row above it.
 *
 * Each leg can be entered as a trigger price or as ROE% on the margin
 * (decision 9: a toggle, no preset chips). In ROE% mode the field shows the
 * trigger price it stands for, so the number the executor will act on is
 * never hidden behind a percentage.
 */
import {
  OrderFormTpsl,
  TpslEntryMode,
  TpslRoeRef,
} from "../../../hooks/tpsl/useOrderFormTpsl";
import { HintText, UnitToggle } from "../../shared/NumericField";
import { Input } from "../../shared/Input";
import TooltipExplanation from "../../shared/Tooltip";
import { Label } from "../style.info";
import {
  TpslCheckboxLabel,
  TpslCheckboxLabelDisabled,
  TpslInputCell,
  TpslInputsRow,
} from "./style.tradeform";

const TPSL_LABEL = "Take Profit / Stop Loss";

const MODES = [
  { id: "price", label: "Price" },
  { id: "roe", label: "ROE %" },
];

/**
 * What the user must know before ticking TP/SL here: an existing TP/SL on the
 * position is REPLACED (the executor holds one set per position), and on a
 * resting limit order the triggers are sent now, not when the order fills.
 */
export function TpslNotes({
  replacesExisting,
  restingLimit,
}: {
  replacesExisting?: boolean;
  restingLimit?: boolean;
}) {
  return (
    <>
      {replacesExisting ? (
        <HintText role="note" data-testid="tpsl-replaces-existing">
          This position already has TP/SL. Setting it here replaces it, sized to this order.
        </HintText>
      ) : null}
      {restingLimit ? (
        <HintText role="note" data-testid="tpsl-sent-now">
          TP/SL is sent as soon as this limit order is placed, not when it fills.
        </HintText>
      ) : null}
    </>
  );
}

export default function TpslControls({
  tpsl,
  roeRef,
  replacesExisting,
  restingLimit,
}: {
  tpsl: OrderFormTpsl;
  /** What ROE% is measured from; drives the preview and the Price <-> ROE% carry-over. */
  roeRef?: TpslRoeRef;
  /** The position this order adds to already has TP/SL legs. */
  replacesExisting?: boolean;
  /** The order is a limit order, which may rest on the book. */
  restingLimit?: boolean;
}) {
  // Gated on the chain having an off-chain trigger executor (ChainConfig.supportsTpsl
  // plus a verifying contract to sign against). Where one exists the checkbox is
  // live; elsewhere it stays a disabled teaser, because accepting a TP/SL that
  // nothing can trigger is worse than not offering it.
  if (!tpsl.supported) {
    return (
      <TooltipExplanation
        title={TPSL_LABEL}
        explanation="Not available on this network yet"
        renderContent={({ ref, ...triggerHandler }) => (
          <TpslCheckboxLabelDisabled ref={ref} {...triggerHandler}>
            <input type="checkbox" disabled readOnly />
            {TPSL_LABEL}
          </TpslCheckboxLabelDisabled>
        )}
      />
    );
  }

  return (
    <>
      <TpslCheckboxLabel>
        <input type="checkbox" checked={tpsl.enabled} onChange={tpsl.toggle} />
        {TPSL_LABEL}
      </TpslCheckboxLabel>
      {/* Both legs are optional — filling only one creates only that leg. */}
      {tpsl.enabled && (
        <>
          <UnitToggle
            units={MODES}
            value={tpsl.mode}
            onChange={(id) => tpsl.setMode(id as TpslEntryMode, roeRef)}
            ariaLabel="Take profit and stop loss entered as"
          />
          {tpsl.mode === "price" ? (
            <TpslInputsRow>
              <TpslInputCell>
                <Label>TP Price</Label>
                <Input
                  placeholder="0.00"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  aria-label="Take profit trigger price"
                  value={tpsl.tpPrice}
                  onChange={(e) => tpsl.setTpPrice(e.target.value)}
                />
              </TpslInputCell>
              <TpslInputCell>
                <Label>SL Price</Label>
                <Input
                  placeholder="0.00"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  aria-label="Stop loss trigger price"
                  value={tpsl.slPrice}
                  onChange={(e) => tpsl.setSlPrice(e.target.value)}
                />
              </TpslInputCell>
            </TpslInputsRow>
          ) : (
            <RoeInputs tpsl={tpsl} roeRef={roeRef} />
          )}
          <TpslNotes replacesExisting={replacesExisting} restingLimit={restingLimit} />
        </>
      )}
    </>
  );
}

/** The two ROE% fields, each with the trigger price it resolves to. */
function RoeInputs({ tpsl, roeRef }: { tpsl: OrderFormTpsl; roeRef?: TpslRoeRef }) {
  const resolved = tpsl.resolve(roeRef);
  const preview = (typed: string, price?: string) =>
    typed.trim() === "" ? null : (
      <HintText>{price ? `Trigger ≈ ${price}` : "Trigger —"}</HintText>
    );
  return (
    <>
      <TpslInputsRow>
        <TpslInputCell>
          <Label>TP ROE %</Label>
          <Input
            placeholder="0"
            type="number"
            inputMode="decimal"
            min="0"
            aria-label="Take profit, ROE percent"
            value={tpsl.tpRoe}
            onChange={(e) => tpsl.setTpRoe(e.target.value)}
          />
          {preview(tpsl.tpRoe, resolved.tp)}
        </TpslInputCell>
        <TpslInputCell>
          <Label>SL ROE %</Label>
          <Input
            placeholder="0"
            type="number"
            inputMode="decimal"
            min="0"
            aria-label="Stop loss, ROE percent"
            value={tpsl.slRoe}
            onChange={(e) => tpsl.setSlRoe(e.target.value)}
          />
          {preview(tpsl.slRoe, resolved.sl)}
        </TpslInputCell>
      </TpslInputsRow>
      {resolved.issue ? <HintText role="alert">{resolved.issue}</HintText> : null}
    </>
  );
}
